import { getAPIManagerInstance } from '../apiManager';
import { DECKARD_API_URL, httpMethodFunctions } from '../common';
import {
  withPaymentErrorFallback,
  withPayGenericErrorFallback,
} from './paymentErrors';
import {
  GENERIC_PAYMENT_ERROR,
  PAY_ACH_ERROR,
} from '../../components/payment/constants';
import {
  ACHDirectDebitPaymentRequest,
  ACHDirectDebitPaymentRequestOld,
  ACHDirectDebitPaymentResponse,
  CardConnectApiData,
  CheckPaymentRequest,
  CheckPaymentRequestOld,
  ChequePaymentResponse,
  CheckPaymentStatusResponse,
  CreditCardPaymentRequest,
  CreditCardPaymentRequestOld,
  CreditCardPaymentResponse,
  CyberSourceApiData,
  GenericPaymentRequest,
  GenericPaymentRequestOld,
  GenericPaymentsResponse,
  PayNothingRequest,
  PayNothingRequestOld,
  PayNothingResponse,
  PostbackResponse,
  PostbackStatus,
  StripePublicKeyResponse,
  StripePublicKeyResponseOld,
} from 'src/types/payment';

export const getStripePublicKey = async (): Promise<StripePublicKeyResponse> =>
  withPaymentErrorFallback(async () => {
    const { data } = await httpMethodFunctions.get<StripePublicKeyResponseOld>(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/stripe-public`
    );
    const oldData: StripePublicKeyResponseOld = data;
    return {
      stripePublishableKey: oldData.stripePublishableKey,
      totStripePublishableKey: oldData.tot?.stripePublishableKey ?? '',
    };
  }, GENERIC_PAYMENT_ERROR);

export const payGeneric = async (
  request: Omit<GenericPaymentRequest, 'source'>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _authToken: string
): Promise<GenericPaymentsResponse> => {
  const oldRequest: GenericPaymentRequestOld = {
    email: request.email,
    paymentId: String(request.payment_id),
    signatory: request.signatory,
  };
  return withPayGenericErrorFallback(async () => {
    const { data } = await httpMethodFunctions.post<
      GenericPaymentRequestOld,
      GenericPaymentsResponse
    >(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payGeneric`,
      oldRequest
    );
    return data;
  }, GENERIC_PAYMENT_ERROR);
};

export const payByCreditCard = async (
  request: Omit<CreditCardPaymentRequest, 'source'>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _authToken: string
): Promise<CreditCardPaymentResponse> => {
  const oldRequest: CreditCardPaymentRequestOld = {
    email: request.email,
    paymentId: String(request.payment_id),
    signatory: request.signatory,
  };
  return withPaymentErrorFallback(async () => {
    const { data } = await httpMethodFunctions.post<
      CreditCardPaymentRequestOld,
      CreditCardPaymentResponse
    >(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payCC`,
      oldRequest
    );
    return data;
  }, GENERIC_PAYMENT_ERROR);
};

export const payByACHDirectDebit = async (
  request: Omit<ACHDirectDebitPaymentRequest, 'source'>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _authToken: string
): Promise<ACHDirectDebitPaymentResponse> => {
  const oldRequest: ACHDirectDebitPaymentRequestOld = {
    email: request.email,
    paymentId: String(request.payment_id),
    signatory: request.signatory,
  };
  return withPaymentErrorFallback(async () => {
    const { data } = await httpMethodFunctions.post<
      ACHDirectDebitPaymentRequestOld,
      ACHDirectDebitPaymentResponse
    >(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payACHDirectDebit`,
      oldRequest
    );
    return data;
  }, PAY_ACH_ERROR);
};

export const payByCheck = async (
  request: Omit<CheckPaymentRequest, 'source'>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _authToken: string
): Promise<ChequePaymentResponse> => {
  const oldRequest: CheckPaymentRequestOld = {
    email: request.email,
    paymentId: String(request.payment_id),
    signatory: request.signatory,
  };
  const { data } = await httpMethodFunctions.post<
    CheckPaymentRequestOld,
    ChequePaymentResponse
  >(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payCheck`,
    oldRequest
  );
  return data;
};

export const payNothing = async (
  request: Omit<PayNothingRequest, 'source' | 'subtotal'>,
  accessToken: string
): Promise<PayNothingResponse> => {
  const oldRequest: PayNothingRequestOld = {
    email: request.email,
    paymentId: String(request.payment_id),
    signatory: request.signatory,
  };
  const { data } = await httpMethodFunctions.post<
    PayNothingRequestOld,
    PayNothingResponse
  >(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/payNothing`,
    oldRequest,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return data;
};

export const checkTaxPayment = async (
  authToken: string,
  paymentID: string
): Promise<CheckPaymentStatusResponse> => {
  try {
    const { data } = await httpMethodFunctions.get<CheckPaymentStatusResponse>(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/checkTotPayment/${paymentID}`,
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
    return data;
  } catch (error) {
    console.error('checkTaxPayment error', error);
    throw new Error('Failed to check tax payment');
  }
};

export const checkLicensePayment = async (
  authToken: string,
  paymentID: string
): Promise<CheckPaymentStatusResponse> => {
  const { data } = await httpMethodFunctions.get<CheckPaymentStatusResponse>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/checkRenewalPayment/${paymentID}`,
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );
  return data;
};

export const cybersourcePostback = async (
  data: CyberSourceApiData
): Promise<PostbackResponse> =>
  withPaymentErrorFallback(async () => {
    const { data: response } = await httpMethodFunctions.post<
      CyberSourceApiData,
      PostbackResponse
    >(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/cyberSourceInitiatePayment`,
      data
    );
    // The legacy CyberSource API returns 'AUTHORIZED' but the new model uses COMPLETE — normalize here so callers always see a consistent status.
    return {
      ...response,
      status:
        response.status === PostbackStatus.AUTHORIZED
          ? PostbackStatus.COMPLETE
          : response.status,
    };
  }, GENERIC_PAYMENT_ERROR);

export const cardConnectPostback = async (
  data: CardConnectApiData
): Promise<PostbackResponse> =>
  withPaymentErrorFallback(async () => {
    const { data: response } = await httpMethodFunctions.post<
      CardConnectApiData,
      PostbackResponse
    >(
      `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/cardConnectPostback`,
      data
    );

    // The legacy API returns 'success' but the new model uses COMPLETE — normalize here so callers always see a consistent status.
    return {
      ...response,
      status:
        response.status === PostbackStatus.SUCCESS
          ? PostbackStatus.COMPLETE
          : response.status,
    };
  }, GENERIC_PAYMENT_ERROR);

export const eunaCancelPayment = async (
  paymentId: string,
  webhookVerificationKey: string
): Promise<{ cancelled: boolean }> => {
  const { data } = await httpMethodFunctions.get<{ cancelled: boolean }>(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/eunaCancelPayment`,
    {
      params: {
        payment_id: paymentId,
        webhook_verification_key: webhookVerificationKey,
      },
    }
  );
  return data;
};

export const snapPayPostback = async (
  paymentId: string,
  token: string,
  paymentType: 'CC' | 'ACH'
): Promise<PostbackResponse> => {
  const { data } = await httpMethodFunctions.post(
    `${DECKARD_API_URL}/${getAPIManagerInstance().getPlaceUrl()}/snappayPostback`,
    { paymentId, token, paymentType }
  );
  return data as PostbackResponse;
};
